import { onSnapshot } from 'firebase/firestore'
import {
  getProductsCollectionSnapShot,
  productsColl,
  saveProduct,
  deleteProduct
} from '@/firebase-firestore'
import type { AccessItem, Categorie, Product, ProductDoc, ShopCategorie } from '@/types'
import type { DocumentData } from 'firebase/firestore'


export default {
  namespaced: true,
  strict: process.env.NODE_ENV !== 'production',
  state: {
    products: null,
    productListener: null,
    categories: [
      { 
        id: 'bookings', name: 'Reservas', 
        hasBookings: 'bookings', hasPayments: true 
      },
      { 
        id: 'shop', name: 'Tienda', 
        hasQty: true 
      },
      { 
        id: 'spaces', name: 'Espacios', 
        adminOnly: true, hasPayments: true 
      },
      { 
        id: 'academies', name: 'Academias', 
        productsHasBookings: 'bookings', adminOnly: true, hasPayments: true 
      },
      { 
        id: 'subscriptions', name: 'Hoopers', 
        hasPayments: true 
      },
      { 
        id: 'sponsors', name: 'Patrocinadores', 
        adminOnly: true, hasPayments: true 
      },
      { 
        id: 'others', name: 'Otros'
      }
    ] as Categorie[],
    shopCategories: [
      { id: 'drinks', name: 'Bebida' },
      { id: 'food', name: 'Comida' },
      { id: 'goods', name: 'Goodies' }
    ] as ShopCategorie[],
  },
  mutations: {
    setProducts (state: any, products: DocumentData[]) {
      state.products = products
        .filter(Boolean)
    },
    setProductsListener (state: any, listener: number) {
      state.productListener = listener
    }
  },
  getters: {
    isProductsReady: (state: any): boolean => {
      return Array.isArray(state.products)
    },
    products: (state: any, getters: any): Product[] => {
      return getters.isProductsReady
        ? state.products
          .map((doc:DocumentData): Product => {
            const {
              ...obj
            } = doc
            const row = obj as ProductDoc
            const categorie = getters.getCategorie(row.categorieId) as Categorie
            const diezIdx =  Boolean(categorie.hasBookings) ? row.productName.indexOf('#') : -1
            return {
              ...row,
              amountDefault: row.amountDefault,
              shortName: diezIdx > -1
                  ? row.productName.substring(diezIdx, diezIdx + 2)
                  : row.productName.substring(0,1),
            } as Product
          })
        : []
    },
    uiProducts: (state: any, getters: any, rootState: any, rootGetters: any): Product[] => {
      return getters.products
        .filter((p: Product) => {
          const categorie = getters.getCategorie(p.categorieId) as Categorie
          return rootGetters['user/userAccess'] === 'admin' || !categorie.adminOnly
        })
        .filter((p: Product) => !p.redirect)
    },
    uiCategories: (state: any, getters: any, rootState: any, rootGetters: any): Categorie[] => {
      return state.categories
        .filter((c: Categorie) => getters.getProductsForCategory(c.id).length)
        .filter((c: Categorie) => !c.adminOnly || rootGetters['user/isAdmin'])
    },
    getProduct: (state: any, getters: any) => (productId: string): Product | undefined => {
      let result = getters.products.find((p: Product) => p.productId === productId)
      if (!result) {
        console.warn('no product found', productId)
        return undefined
      }
      while (typeof result.redirect === 'string') {
        result = getters.getProduct(result.redirect)
      }
      const categorie = state.categories.find((c: Categorie) => c.id === result.categorieId)
      return {
        ...result,
        categorie
      }
    },
    getCategorie: (state: any) => (categorieId: string): Categorie => {
      return state.categories
        .find((c: Categorie) => c.id === categorieId)
    },
    isUiCategorie: (state: any, getters: any) => (categorieId: string):boolean => {
      return getters.uiCategories
        .findIndex((c: Categorie) => c.id === categorieId) > -1
    },
    getProductsForCategory: (state: any, getters: any) => (categorieId: string):Product[] => {
      return getters.uiProducts
        .filter((p: Product) => p.categorieId === categorieId)
    },
    bookingProducts: (state: any, getters: any): Product[] => {
      return getters.getProductsForCategory('bookings')
    },
    // courtsProduct: return product 24
    courtsProducts: (state: any, getters: any): Product[] => {
      return getters.getProductsForCategory('bookings')
    },
    shopCategories: (state: any, getters: any): Categorie => {
      return state.shopCategories
    },
    shopProducts: (state: any, getters: any): Product[] => {
      return getters.getProductsForCategory('shop')
    },
    bookingsAccessOnlyProducts: (state: any, getters: any): Product[] => {
      return state.categories
        .filter((c: Categorie) => c.adminOnly)
        .reduce((acc:Product[], c: Categorie) => {
          acc.push(...getters.getProductsForCategory(c.id))
          return acc
        }, [])
    }
  },
  actions: {
    async saveProduct (commit: any, payload: any) {
      await saveProduct(payload)
    },
    async deleteProduct (commit: any, productId: string) {
      await deleteProduct(productId)
    },
    releaseProducts ({ commit, state }: { commit: any, state: any }) {
      if (state.productListener) {
        state.productListener()
        commit('setProductsListener', null)
        commit('setProducts', [])
        console.warn('Removed listener for products')
      }
    },
    getProducts ({ commit, dispatch, state }: { commit: any, dispatch: any, state: any }) {
      if (state.productListener) {
        dispatch('releaseProducts')
      }
      const q = productsColl
      const listener = onSnapshot(q, { includeMetadataChanges: true }, async (snapshot) => {
        console.time('getProducts.onSnapshot')
        const products = await getProductsCollectionSnapShot(snapshot)
        commit('setProducts', products)
        console.timeEnd('getProducts.onSnapshot')
      })
      commit('setProductsListener', listener)
    }
  }
}
